import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import StyledTableRow from "app/Components/Tables/StyledTableRow";
import PrimaryTableCell from "app/Components/Tables/PrimaryTableCell";
import TableBody from "@mui/material/TableBody";

import { GameData, RootState, GamelistModalDetails, ModalDetails } from "types";
import GameInfoBtn from "app/Components/Buttons/FilledBtns/GameInfoBtn";
import SendReqBtn from "app/Components/Buttons/FilledBtns/SendReqBtn";
import calcDistance from "utils/calcDistance";
import sortGamesByStartTime from "utils/sortGamesByStartTime";
import { DayMap } from "utils/constants";

const tableCellStyles = {
  textAlign: "center" as const,
  margin: "auto",
  padding: "auto",
};

interface StatusTableCellProps {
  gameData: GameData;
}

const StatusTableCell = ({ gameData }: StatusTableCellProps) => {
  if (["Running, Delayed"].includes(gameData.status)) {
    return (
      <PrimaryTableCell sx={{ ...tableCellStyles, color: "red" }}>
        {gameData.status}
      </PrimaryTableCell>
    );
  }
  return (
    <PrimaryTableCell sx={tableCellStyles}>
      {`Starts ${DayMap[gameData.startDay as keyof typeof DayMap] || 'Unknown Day'} ${gameData.startTime}`}
    </PrimaryTableCell>
  );
};

interface CashGameTableBodyProps {
  data: Record<string, GameData>;
  setModalDets: (dets: GamelistModalDetails) => void;
  setGameData: (data: GameData) => void;
}

const CashGameTableBody = ({ data, setModalDets, setGameData }: CashGameTableBodyProps) => {
  const navigate = useNavigate();

  const locationState = useSelector((state: RootState) => state.location);
  const { lat, lng, country } = locationState || {};

  const gameIds = sortGamesByStartTime(data);

  // Adapter function to convert ModalDetails to GamelistModalDetails
  const handleModalChange = (modalDetails: ModalDetails) => {
    setModalDets({
      open: modalDetails.open,
      status: modalDetails.status,
    });
  };

  return (
    <TableBody>
      {gameIds.map((gameId) => {
        const gameData = data[gameId];

        return (
          <StyledTableRow key={gameId}>
            <PrimaryTableCell
              sx={{
                ...tableCellStyles,
                cursor: "pointer",
                maxWidth: window.innerWidth / 4,
                wordWrap: "break-word",
              }}
              onClick={() => {
                navigate(`/games/${gameId}`);
              }}
            >
              {gameData.gameName}
            </PrimaryTableCell>
            <PrimaryTableCell sx={tableCellStyles}>
              {gameData.game}
            </PrimaryTableCell>
            <PrimaryTableCell sx={tableCellStyles}>
              {gameData.stakes}
            </PrimaryTableCell>
            <StatusTableCell gameData={gameData} />
            <PrimaryTableCell sx={tableCellStyles}>
              {/* //todo: need to check if user is in US or not  */}
              {`${calcDistance(
                lat || 0,
                lng || 0,
                gameData.lat || 0,
                gameData.lng || 0,
                country || "United States",
              )}mi`}
            </PrimaryTableCell>
            <PrimaryTableCell
              sx={tableCellStyles}
              aria-label="table-waitlist"
            >
              {Object.keys(gameData.waitlist).length}
            </PrimaryTableCell>
            <PrimaryTableCell sx={tableCellStyles}>
              <SendReqBtn
                action={() => setGameData(gameData)}
                gameId={gameId}
                cb={handleModalChange}
              />
              <GameInfoBtn gameId={gameId} />
            </PrimaryTableCell>
          </StyledTableRow>
        );
      })}
    </TableBody>
  );
};

export default CashGameTableBody;
